
import React, { useState, useEffect } from "react";
import { PlayCircle, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface WebhookConfig {
  url: string;
  method: string;
  headers: { key: string; value: string }[];
  body: string;
}

interface WebhookFormProps {
  initialConfig?: WebhookConfig | null;
  onSaveConfig: (config: WebhookConfig) => void;
}

export const WebhookForm = ({ initialConfig = null, onSaveConfig }: WebhookFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [body, setBody] = useState("");
  
  // Initialize form with existing configuration if provided
  useEffect(() => {
    if (initialConfig) {
      setWebhookUrl(initialConfig.url || "");
      setBody(initialConfig.body || "");
      
      if (initialConfig.headers && initialConfig.headers.length > 0) {
        setHeaders(initialConfig.headers);
      }
    }
  }, [initialConfig]);
  
  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const removeHeader = (index: number) => {
    const newHeaders = [...headers];
    newHeaders.splice(index, 1);
    setHeaders(newHeaders);
  };

  const updateHeaderKey = (index: number, value: string) => {
    const newHeaders = [...headers];
    newHeaders[index].key = value;
    setHeaders(newHeaders);
  };

  const updateHeaderValue = (index: number, value: string) => {
    const newHeaders = [...headers];
    newHeaders[index].value = value;
    setHeaders(newHeaders);
  };

  const sendWebhook = async () => {
    if (!webhookUrl) {
      toast({
        title: "Error",
        description: "Please enter a webhook URL",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Preparar los headers
      const headerObj: Record<string, string> = {};
      headers.forEach((header) => {
        if (header.key && header.value) {
          headerObj[header.key] = header.value;
        }
      });

      // Configurar la petición
      const options: RequestInit = {
        method: "POST",
        headers: {
          ...headerObj,
          "Content-Type": "application/json",
        },
      };

      // Añadir body si es necesario
      if (body) {
        try {
          options.body = body;
        } catch (e) {
          toast({
            title: "Body Error",
            description: "Invalid JSON body format",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }

      // Enviar la petición
      const response = await fetch(webhookUrl, options);
      const data = await response.text();

      // Mostrar resultado
      toast({
        title: `Response (${response.status})`,
        description: (
          <pre className="mt-2 w-full max-h-40 overflow-auto rounded-md bg-slate-950 p-4">
            <code className="text-white">{data || "No response"}</code>
          </pre>
        ),
      });
    } catch (error) {
      toast({
        title: "Send Error",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const saveWebhookConfig = () => {
    // Filter out empty headers
    const filteredHeaders = headers.filter(header => header.key && header.value);
    
    // Create the webhook configuration object
    const webhookConfig: WebhookConfig = {
      url: webhookUrl,
      method: "POST",
      headers: filteredHeaders,
      body: body
    };
    
    // Call the parent component's save function with the config
    if (typeof onSaveConfig === 'function') {
      onSaveConfig(webhookConfig);
      
      toast({
        title: "Configuration Saved",
        description: "Webhook configuration has been saved successfully",
      });
    }
  };

  return (
    <div className="w-full">
      <Card>
        <CardContent className="space-y-6 mt-5 p-4 md:p-6">
          <div className="space-y-2">
            <Label htmlFor="webhook-url">Webhook URL</Label>
            <Input
              id="webhook-url"
              placeholder="https://api.example.com/webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="method">HTTP Method</Label>
            <div className="p-2 bg-gray-50 rounded border text-sm text-gray-700">
              POST
            </div>
            <div className="text-sm text-muted-foreground">
              All webhooks use the POST method
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <Label>Headers</Label>
              <Button variant="outline" size="sm" onClick={addHeader} type="button" className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Header
              </Button>
            </div>
            <div className="space-y-3">
              {headers.map((header, index) => (
                <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Input
                    placeholder="Header name"
                    value={header.key}
                    onChange={(e) => updateHeaderKey(index, e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Header value"
                    value={header.value}
                    onChange={(e) => updateHeaderValue(index, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeHeader(index)}
                    type="button"
                    className="h-10 w-10 self-center sm:self-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Request Body (JSON)</Label>
            <Textarea
              id="body"
              placeholder='{"key": "value"}'
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="font-mono h-32 w-full resize-none"
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row justify-between gap-3 p-4 md:p-6">
          <Button onClick={sendWebhook} disabled={isLoading} variant="outline" className="w-full sm:w-auto">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <PlayCircle className="mr-2 h-4 w-4" />
                Test Webhook
              </>
            )}
          </Button>

          <Button onClick={saveWebhookConfig} disabled={isLoading} className="w-full sm:w-auto">
            {isLoading ? (
              "Saving..."
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Configuration
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
